import React, { useEffect, useMemo, useState } from "react";
import {
  Wallet,
  Receipt,
  TrendingUp,
  Coins,
  XCircle,
  CheckCircle2,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  CircleDollarSign,
  CalendarDays,
  Info,
} from "lucide-react";
import { Navbar } from "../components/app/Navbar";
import { Footer } from "../components/app/Footer";
import { FinancialsExports } from "../components/app/FinancialsExports";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useT } from "../context/LanguageContext";
import api from "../lib/api";
import { toast } from "sonner";

const CATEGORIES = [
  "gas",
  "supplies",
  "ads",
  "equipment",
  "payroll",
  "insurance",
  "vehicle",
  "software",
  "meals",
  "other",
];

// Meta for the financial "Learn more" modal. Each entry maps a card key
// to its translation keys + a value resolver that runs against the live
// summary payload. The dialog stays mounted once and swaps content via
// `activeCard` — same pattern as BusinessInsights.
const CARD_INFO = {
  monthRevenue: {
    labelKey: "fin.revenue",
    explainKey: "fin.modal.revenue.explain",
    tipKey: "fin.modal.revenue.tip",
    value: ({ rev, fmtUSD }) => fmtUSD(rev.this_month),
  },
  monthExpenses: {
    labelKey: "fin.expenses",
    explainKey: "fin.modal.expenses.explain",
    tipKey: "fin.modal.expenses.tip",
    value: ({ exp, fmtUSD }) => fmtUSD(exp.this_month),
  },
  monthNet: {
    labelKey: "fin.estimatedNet",
    explainKey: "fin.modal.net.explain",
    tipKey: "fin.modal.net.tip",
    value: ({ profit, fmtUSD }) => fmtUSD(profit.this_month_estimated_net),
  },
  ytdRevenue: {
    labelKey: "fin.revenue",
    explainKey: "fin.modal.revenue.explain",
    tipKey: "fin.modal.revenue.tip",
    value: ({ rev, fmtUSD }) => fmtUSD(rev.year_to_date),
  },
  ytdExpenses: {
    labelKey: "fin.expenses",
    explainKey: "fin.modal.expenses.explain",
    tipKey: "fin.modal.expenses.tip",
    value: ({ exp, fmtUSD }) => fmtUSD(exp.year_to_date),
  },
  ytdNet: {
    labelKey: "fin.estimatedNet",
    explainKey: "fin.modal.net.explain",
    tipKey: "fin.modal.net.tip",
    value: ({ profit, fmtUSD }) => fmtUSD(profit.year_to_date_estimated_net),
  },
  potential: {
    labelKey: "fin.potentialRevenue",
    explainKey: "fin.modal.potential.explain",
    tipKey: "fin.modal.potential.tip",
    value: ({ rev, fmtUSD }) => fmtUSD(rev.potential_revenue),
  },
  cancelled: {
    labelKey: "fin.cancelledRevenue",
    explainKey: "fin.modal.cancelled.explain",
    tipKey: "fin.modal.cancelled.tip",
    value: ({ rev, fmtUSD }) => fmtUSD(rev.cancelled_revenue),
  },
  closed: {
    labelKey: "fin.closedJobs",
    explainKey: "fin.modal.closed.explain",
    tipKey: "fin.modal.closed.tip",
    value: ({ rev }) => String(rev.closed_jobs_count ?? 0),
  },
};

const fmtUSD = (n) =>
  (typeof n === "number" ? n : 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDateLocal = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const todayInputValue = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const MetricCard = ({ icon: Icon, label, value, helper, tone = "emerald", testid, onClick }) => {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    red: "bg-red-50 text-red-700 border-red-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  const valueTone = {
    emerald: "text-emerald-700",
    red: "text-red-700",
    amber: "text-amber-700",
    slate: "text-slate-900",
  };
  const inner = (
    <>
      <div className="flex items-center gap-3">
        <span className={`w-9 h-9 rounded-lg border flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="w-4 h-4" />
        </span>
        <span className="text-sm font-medium text-gray-600 flex items-center gap-1">
          {label}
          {onClick ? (
            <Info
              className="w-3.5 h-3.5 text-gray-400 group-hover:text-emerald-700 transition-colors shrink-0"
              aria-hidden="true"
            />
          ) : null}
        </span>
      </div>
      <div className={`mt-3 font-display font-semibold text-2xl tracking-tight ${valueTone[tone]}`} data-testid={`${testid}-value`}>
        {value}
      </div>
      {helper ? (
        <p className="mt-1 text-xs text-gray-500 leading-relaxed">{helper}</p>
      ) : null}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group rounded-xl border border-gray-200 bg-white p-5 shadow-sm text-left w-full transition-all duration-150 hover:border-emerald-300 hover:shadow-md hover:-translate-y-[1px] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 active:translate-y-0"
        data-testid={testid}
      >
        {inner}
      </button>
    );
  }
  return (
    <div
      className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
      data-testid={testid}
    >
      {inner}
    </div>
  );
};

const ExpenseForm = ({ open, onClose, onSaved, initial, defaultCategory, t }) => {
  const isEdit = Boolean(initial?.id);
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("supplies");
  const [date, setDate] = useState(todayInputValue());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setTitle(initial.title || "");
      setAmount(initial.amount != null ? String(initial.amount) : "");
      setCategory(initial.category || "supplies");
      setDate(initial.expense_date ? initial.expense_date.slice(0, 10) : todayInputValue());
      setNote(initial.note || "");
    } else {
      setTitle("");
      setAmount("");
      // When opening "Add expense" without an initial row, respect the
      // active filter category if the user has narrowed the list, else
      // fall back to the friendly default ("supplies").
      setCategory(
        defaultCategory && CATEGORIES.includes(defaultCategory)
          ? defaultCategory
          : "supplies",
      );
      setDate(todayInputValue());
      setNote("");
    }
    setErr("");
  }, [open, initial, defaultCategory]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const trimmedTitle = title.trim();
    const amt = parseFloat(amount);
    if (!trimmedTitle) {
      setErr(t("fin.errorTitle"));
      return;
    }
    if (!Number.isFinite(amt) || amt <= 0) {
      setErr(t("fin.errorAmount"));
      return;
    }
    if (!CATEGORIES.includes(category)) {
      setErr(t("fin.errorCategory"));
      return;
    }
    if (!date) {
      setErr(t("fin.errorDate"));
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: trimmedTitle,
        amount: amt,
        category,
        expense_date: new Date(`${date}T12:00:00Z`).toISOString(),
        note: note.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/expenses/${initial.id}`, body);
      } else {
        await api.post("/expenses", body);
      }
      toast.success(t("fin.savedToast"));
      onSaved();
      onClose();
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : t("fin.errorGeneric"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="expense-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("fin.editExpense") : t("fin.addExpense")}</DialogTitle>
          <DialogDescription>{t("fin.expensesSubtitle")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="exp-title">{t("fin.expenseTitle")}</Label>
            <Input
              id="exp-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("fin.expenseTitlePlaceholder")}
              autoFocus
              data-testid="expense-form-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="exp-amount">{t("fin.expenseAmount")}</Label>
              <Input
                id="exp-amount"
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                data-testid="expense-form-amount"
              />
            </div>
            <div>
              <Label htmlFor="exp-date">{t("fin.expenseDate")}</Label>
              <Input
                id="exp-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="expense-form-date"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed -mt-1" data-testid="expense-form-date-helper">
            {t("fin.expenseDateHelper")}
          </p>
          <div>
            <Label>{t("fin.expenseCategory")}</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="expense-form-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`expense-cat-opt-${c}`}>
                    {t(`fin.cat.${c}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="exp-note">{t("fin.expenseNote")}</Label>
            <Textarea
              id="exp-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("fin.expenseNotePlaceholder")}
              rows={2}
              data-testid="expense-form-note"
            />
          </div>
          {err ? (
            <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="expense-form-error">
              {err}
            </p>
          ) : null}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
              data-testid="expense-form-cancel"
            >
              {t("fin.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-green-800 hover:bg-green-900 text-white"
              data-testid="expense-form-save"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("fin.saving")}
                </>
              ) : (
                t("fin.save")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Financials() {
  const { t } = useT();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  // Which financial card's "Learn more" modal is open. Single dialog
  // instance at the page level — swaps content via this key.
  const [activeCard, setActiveCard] = useState(null);

  const loadAll = async () => {
    try {
      const [sumRes, expRes] = await Promise.all([
        api.get("/dashboard/financials"),
        api.get("/expenses"),
      ]);
      setSummary(sumRes.data);
      setExpenses(expRes.data || []);
    } catch (e) {
      // silent — the page will render an empty state on persistent failure
      console.error("financials load failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredExpenses = useMemo(() => {
    if (filterCat === "all") return expenses;
    return expenses.filter((e) => e.category === filterCat);
  }, [expenses, filterCat]);

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (exp) => {
    setEditing(exp);
    setFormOpen(true);
  };
  const doDelete = async (exp) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t("fin.confirmDelete"))) return;
    setDeletingId(exp.id);
    try {
      await api.delete(`/expenses/${exp.id}`);
      toast.success(t("fin.deletedToast"));
      await loadAll();
    } catch (e) {
      toast.error(t("fin.errorGeneric"));
    } finally {
      setDeletingId(null);
    }
  };

  const rev = summary?.revenue || {};
  const exp = summary?.expenses || {};
  const profit = summary?.profit || {};

  return (
    <div className="min-h-screen flex flex-col bg-[#f9f9f8]" data-testid="financials-page">
      <Navbar />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight" data-testid="financials-title">
              {t("fin.title")}
            </h1>
            <p className="mt-2 text-gray-500 max-w-2xl">{t("fin.subtitle")}</p>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-2 self-start sm:self-auto">
            <span className="inline-flex items-center gap-2 text-xs font-medium text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1.5" data-testid="financials-estimate-badge">
              <CircleDollarSign className="w-3.5 h-3.5" />
              {t("fin.estimateBadge")}
            </span>
            <FinancialsExports />
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm animate-pulse">
                <div className="h-4 w-24 bg-gray-100 rounded mb-3" />
                <div className="h-7 w-32 bg-gray-100 rounded" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* THIS MONTH */}
            <section className="mb-8" data-testid="fin-section-this-month">
              <h2 className="font-display text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                {t("fin.sectionThisMonth")}
                {summary?.month_label ? <span className="ml-2 text-gray-400 normal-case font-medium">· {summary.month_label}</span> : null}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <MetricCard
                  icon={Wallet}
                  label={t("fin.revenue")}
                  value={fmtUSD(rev.this_month)}
                  helper={t("fin.revenueHelper")}
                  tone="emerald"
                  testid="fin-month-revenue"
                  onClick={() => setActiveCard("monthRevenue")}
                />
                <MetricCard
                  icon={Receipt}
                  label={t("fin.expenses")}
                  value={fmtUSD(exp.this_month)}
                  helper={t("fin.expensesHelper")}
                  tone="red"
                  testid="fin-month-expenses"
                  onClick={() => setActiveCard("monthExpenses")}
                />
                <MetricCard
                  icon={TrendingUp}
                  label={t("fin.estimatedNet")}
                  value={fmtUSD(profit.this_month_estimated_net)}
                  helper={t("fin.netHelper")}
                  tone={profit.this_month_estimated_net >= 0 ? "emerald" : "red"}
                  testid="fin-month-net"
                  onClick={() => setActiveCard("monthNet")}
                />
              </div>
            </section>

            {/* YEAR TO DATE */}
            <section className="mb-8" data-testid="fin-section-ytd">
              <h2 className="font-display text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                {t("fin.sectionYearToDate")}
                {summary?.year_label ? <span className="ml-2 text-gray-400 normal-case font-medium">· {summary.year_label}</span> : null}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <MetricCard
                  icon={Wallet}
                  label={t("fin.revenue")}
                  value={fmtUSD(rev.year_to_date)}
                  helper={t("fin.revenueHelper")}
                  tone="emerald"
                  testid="fin-ytd-revenue"
                  onClick={() => setActiveCard("ytdRevenue")}
                />
                <MetricCard
                  icon={Receipt}
                  label={t("fin.expenses")}
                  value={fmtUSD(exp.year_to_date)}
                  helper={t("fin.expensesHelper")}
                  tone="red"
                  testid="fin-ytd-expenses"
                  onClick={() => setActiveCard("ytdExpenses")}
                />
                <MetricCard
                  icon={TrendingUp}
                  label={t("fin.estimatedNet")}
                  value={fmtUSD(profit.year_to_date_estimated_net)}
                  helper={t("fin.netHelper")}
                  tone={profit.year_to_date_estimated_net >= 0 ? "emerald" : "red"}
                  testid="fin-ytd-net"
                  onClick={() => setActiveCard("ytdNet")}
                />
              </div>
            </section>

            {/* PIPELINE */}
            <section className="mb-10" data-testid="fin-section-pipeline">
              <h2 className="font-display text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                {t("fin.sectionPipeline")}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <MetricCard
                  icon={Coins}
                  label={t("fin.potentialRevenue")}
                  value={fmtUSD(rev.potential_revenue)}
                  helper={t("fin.potentialHelper")}
                  tone="amber"
                  testid="fin-pipeline-potential"
                  onClick={() => setActiveCard("potential")}
                />
                <MetricCard
                  icon={XCircle}
                  label={t("fin.cancelledRevenue")}
                  value={fmtUSD(rev.cancelled_revenue)}
                  helper={t("fin.cancelledHelper")}
                  tone="slate"
                  testid="fin-pipeline-cancelled"
                  onClick={() => setActiveCard("cancelled")}
                />
                <MetricCard
                  icon={CheckCircle2}
                  label={t("fin.closedJobs")}
                  value={String(rev.closed_jobs_count ?? 0)}
                  helper={t("fin.closedJobsHelper")}
                  tone="emerald"
                  testid="fin-pipeline-closed"
                  onClick={() => setActiveCard("closed")}
                />
              </div>
            </section>

            {/* EXPENSES MANAGEMENT */}
            <section
              className="rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden"
              data-testid="fin-expenses-section"
            >
              <header className="px-6 sm:px-7 py-5 border-b border-gray-100 flex items-start justify-between gap-4 flex-col sm:flex-row sm:items-center">
                <div className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center shrink-0">
                    <Receipt className="w-5 h-5 text-green-800" />
                  </span>
                  <div>
                    <h2 className="font-display text-lg font-semibold text-gray-900 tracking-tight">{t("fin.expensesTitle")}</h2>
                    <p className="text-sm text-gray-500 mt-0.5">{t("fin.expensesSubtitle")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Select value={filterCat} onValueChange={setFilterCat}>
                    <SelectTrigger className="w-full sm:w-48" data-testid="fin-filter-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" data-testid="fin-filter-all">{t("fin.filterAll")}</SelectItem>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`fin-filter-${c}`}>
                          {t(`fin.cat.${c}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={openAdd}
                    className="bg-green-800 hover:bg-green-900 text-white shrink-0"
                    data-testid="fin-add-expense"
                  >
                    <Plus className="w-4 h-4 mr-1.5" />
                    {t("fin.addExpense")}
                  </Button>
                </div>
              </header>

              {filteredExpenses.length === 0 ? (
                <div className="px-6 sm:px-7 py-12 text-center" data-testid="fin-expenses-empty">
                  <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-3">
                    <Receipt className="w-5 h-5 text-emerald-700" />
                  </div>
                  <h3 className="font-display text-base font-semibold text-gray-900">{t("fin.emptyTitle")}</h3>
                  <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">{t("fin.emptyBody")}</p>
                  <Button
                    onClick={openAdd}
                    className="mt-4 bg-green-800 hover:bg-green-900 text-white"
                    data-testid="fin-add-expense-empty"
                  >
                    <Plus className="w-4 h-4 mr-1.5" />
                    {t("fin.addExpense")}
                  </Button>
                </div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm" data-testid="fin-expenses-table">
                      <thead>
                        <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-100">
                          <th className="px-6 py-3">{t("fin.tableTitle")}</th>
                          <th className="px-6 py-3">{t("fin.tableCategory")}</th>
                          <th className="px-6 py-3 text-right">{t("fin.tableAmount")}</th>
                          <th className="px-6 py-3">{t("fin.tableDate")}</th>
                          <th className="px-6 py-3">{t("fin.tableNote")}</th>
                          <th className="px-6 py-3 text-right">{t("fin.tableActions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredExpenses.map((e) => (
                          <tr key={e.id} className="border-b border-gray-50 hover:bg-gray-50/60 transition" data-testid={`fin-expense-row-${e.id}`}>
                            <td className="px-6 py-3 font-medium text-gray-900">{e.title}</td>
                            <td className="px-6 py-3">
                              <span className="inline-flex items-center text-xs font-medium text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
                                {t(`fin.cat.${e.category}`)}
                              </span>
                            </td>
                            <td className="px-6 py-3 text-right font-semibold text-gray-900">{fmtUSD(e.amount)}</td>
                            <td className="px-6 py-3 text-gray-600">
                              <span className="inline-flex items-center gap-1.5">
                                <CalendarDays className="w-3.5 h-3.5 text-gray-400" />
                                {formatDateLocal(e.expense_date)}
                              </span>
                            </td>
                            <td className="px-6 py-3 text-gray-500 max-w-[18rem] truncate">{e.note || ""}</td>
                            <td className="px-6 py-3">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEdit(e)}
                                  data-testid={`fin-expense-edit-${e.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                  <span className="sr-only">{t("fin.editLabel")}</span>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => doDelete(e)}
                                  disabled={deletingId === e.id}
                                  className="text-red-700 hover:text-red-800 hover:bg-red-50"
                                  data-testid={`fin-expense-delete-${e.id}`}
                                >
                                  {deletingId === e.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                  <span className="sr-only">{t("fin.deleteExpense")}</span>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <ul className="md:hidden divide-y divide-gray-100">
                    {filteredExpenses.map((e) => (
                      <li key={e.id} className="px-5 py-4" data-testid={`fin-expense-card-${e.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-gray-900 truncate">{e.title}</p>
                            <div className="mt-1 flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center text-xs font-medium text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
                                {t(`fin.cat.${e.category}`)}
                              </span>
                              <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                                <CalendarDays className="w-3 h-3" />
                                {formatDateLocal(e.expense_date)}
                              </span>
                            </div>
                            {e.note ? <p className="mt-1 text-xs text-gray-500 line-clamp-2">{e.note}</p> : null}
                          </div>
                          <div className="text-right shrink-0">
                            <p className="font-semibold text-gray-900">{fmtUSD(e.amount)}</p>
                            <div className="mt-1 flex items-center justify-end gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEdit(e)}
                                data-testid={`fin-expense-edit-m-${e.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => doDelete(e)}
                                disabled={deletingId === e.id}
                                className="text-red-700 hover:text-red-800 hover:bg-red-50"
                                data-testid={`fin-expense-delete-m-${e.id}`}
                              >
                                {deletingId === e.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          </>
        )}
      </main>

      <ExpenseForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={loadAll}
        initial={editing}
        defaultCategory={filterCat !== "all" ? filterCat : null}
        t={t}
      />

      {/* "Learn more" modal — single instance, swaps content via activeCard. */}
      <Dialog
        open={Boolean(activeCard)}
        onOpenChange={(o) => { if (!o) setActiveCard(null); }}
      >
        <DialogContent className="sm:max-w-md" data-testid="fin-card-modal">
          {activeCard && CARD_INFO[activeCard] ? (
            <>
              <DialogHeader>
                <DialogTitle
                  className="font-display text-xl text-gray-900"
                  data-testid="fin-card-modal-title"
                >
                  {t(CARD_INFO[activeCard].labelKey)}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {t(CARD_INFO[activeCard].explainKey)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 pt-1">
                <div
                  className="rounded-lg border border-emerald-100 bg-emerald-50/60 p-3"
                  data-testid="fin-card-modal-value"
                >
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-800">
                    {t("bi.modal.currentValue")}
                  </p>
                  <p className="font-display text-2xl font-bold text-emerald-900 mt-0.5 leading-tight">
                    {CARD_INFO[activeCard].value({ rev, exp, profit, fmtUSD })}
                  </p>
                </div>

                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
                    {t("bi.modal.explain")}
                  </p>
                  <p
                    className="mt-1 text-sm text-gray-700 leading-relaxed"
                    data-testid="fin-card-modal-explain"
                  >
                    {t(CARD_INFO[activeCard].explainKey)}
                  </p>
                </div>

                <div className="rounded-lg border border-amber-100 bg-amber-50/60 p-3">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-amber-800">
                    {t("bi.modal.tip")}
                  </p>
                  <p
                    className="mt-1 text-sm text-amber-900 leading-relaxed"
                    data-testid="fin-card-modal-tip"
                  >
                    {t(CARD_INFO[activeCard].tipKey)}
                  </p>
                </div>
              </div>

              <DialogFooter className="mt-2">
                <Button
                  type="button"
                  onClick={() => setActiveCard(null)}
                  className="bg-green-800 hover:bg-green-900 text-white"
                  data-testid="fin-card-modal-close"
                >
                  {t("bi.modal.close")}
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
